import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";

function SubDepartmentFormModal({ mode, initialValues, departmentOptions = [], onSubmit, onClose }) {
  const [name, setName] = useState(initialValues?.name || "");
  const [departmentId, setDepartmentId] = useState(
    initialValues?.departmentId && initialValues.departmentId !== "-"
      ? String(initialValues.departmentId)
      : ""
  );
  const [statusId, setStatusId] = useState(
    String(Number(initialValues?.statusId) === 1 ? 1 : 2)
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isEdit = mode === "edit";

  useEffect(() => {
    setName(initialValues?.name || "");
    setDepartmentId(
      initialValues?.departmentId && initialValues.departmentId !== "-"
        ? String(initialValues.departmentId)
        : ""
    );
    setStatusId(String(Number(initialValues?.statusId) === 1 ? 1 : 2));
    setError("");
  }, [initialValues?.name, initialValues?.departmentId, initialValues?.statusId]);

  async function handleSubmit(event) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) {
      setError("Sub Department name is required.");
      return;
    }
    if (trimmedName.length > 100) {
      setError("Sub Department name must be 100 characters or fewer.");
      return;
    }

    if (!departmentId) {
      setError("Department is required.");
      return;
    }

    setSubmitting(true);
    setError("");
    try {
      await onSubmit({
        name: trimmedName,
        departmentId,
        ...(isEdit ? { statusId: Number(statusId) } : {}),
      });
      onClose();
    } catch (submitError) {
      setError(submitError?.message || "Unable to save Sub Department.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!submitting) onClose();
      }}
      fullWidth
      maxWidth="sm"
      aria-labelledby="sub-department-form-title"
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle id="sub-department-form-title">
          {isEdit ? "Edit Sub Department" : "Add Sub Department"}
        </DialogTitle>
        <DialogContent className="flex flex-col gap-4 pt-2">
          <TextField
            autoFocus
            required
            fullWidth
            label="Sub Department Name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ maxLength: 100 }}
            disabled={submitting}
            error={Boolean(error) && !name.trim()}
          />
          <TextField
            select
            required
            fullWidth
            label="Department"
            value={departmentId}
            onChange={(event) => setDepartmentId(event.target.value)}
            disabled={submitting}
            error={Boolean(error) && !departmentId}
          >
            {departmentOptions.map((option) => (
              <MenuItem
                key={option.department_id}
                value={String(option.department_id)}
              >
                {option.department_name}
              </MenuItem>
            ))}
          </TextField>
          {isEdit ? (
            <TextField
              select
              fullWidth
              label="Status"
              value={statusId}
              onChange={(event) => setStatusId(event.target.value)}
              disabled={submitting}
            >
              <MenuItem value="1">Active</MenuItem>
              <MenuItem value="2">Inactive</MenuItem>
            </TextField>
          ) : null}
          {error ? (
            <div role="alert" className="text-sm text-red-600">
              {error}
            </div>
          ) : null}
        </DialogContent>
        <DialogActions className="px-6 pb-4">
          <Button onClick={onClose} disabled={submitting}>
            Cancel Sub Department
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting
              ? "Saving..."
              : isEdit
                ? "Save Sub Department"
                : "Create Sub Department"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default SubDepartmentFormModal;
